import React from 'react';
import { motion } from 'framer-motion';
import { Heart, DollarSign, ShoppingBag, Truck, ThumbsUp, Check } from 'lucide-react';
import PageHeader from '../../components/UI/PageHeader';
import Container from '../../components/UI/Container';
import DonationForm from './DonationForm';
import Button from '../../components/UI/Button';

const Donate: React.FC = () => {
  return (
    <>
      <PageHeader
        title="Donează pentru prietenii blănoși"
        subtitle="Ajută-ne să continuăm misiunea de a oferi adăpost, îngrijire și o șansă la o viață mai bună animalelor fără stăpân"
        image="https://images.pexels.com/photos/1254140/pexels-photo-1254140.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />

      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-bold text-primary mb-6 font-display">Sprijină cauza noastră</h2>
              <p className="text-gray-700 mb-4">
                Adăpostul nostru funcționează în mare parte datorită donațiilor generoase din partea iubitorilor de animale ca tine. Fiecare contribuție, indiferent de mărime, face o diferență semnificativă în viața animalelor pe care le îngrijim.
              </p>
              <p className="text-gray-700 mb-6">
                Donațiile tale ne ajută să asigurăm:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex">
                  <div className="bg-primary-100 p-2 rounded-full mr-3">
                    <ShoppingBag size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Hrană de calitate</h3>
                    <p className="text-sm text-gray-600">Pentru toți locuitorii adăpostului</p>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex">
                  <div className="bg-primary-100 p-2 rounded-full mr-3">
                    <Truck size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Transport</h3>
                    <p className="text-sm text-gray-600">Pentru salvarea animalelor abandonate</p>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex">
                  <div className="bg-primary-100 p-2 rounded-full mr-3">
                    <Heart size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Îngrijire medicală</h3>
                    <p className="text-sm text-gray-600">Tratamente, vaccinuri și operații</p>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex">
                  <div className="bg-primary-100 p-2 rounded-full mr-3">
                    <ThumbsUp size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Îngrijire zilnică</h3>
                    <p className="text-sm text-gray-600">Adăpost, curățenie și personal</p>
                  </div>
                </div>
              </div>

              <div className="bg-primary-100 p-6 rounded-lg mb-8">
                <div className="flex items-start">
                  <DollarSign size={24} className="text-primary mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-primary mb-2">Cum sunt folosite donațiile?</h3>
                    <p className="text-gray-700 mb-3">
                      Suntem dedicați transparenței totale în utilizarea fondurilor. Iată cum sunt distribuite donațiile:
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center">
                        <span className="text-gray-700 w-40">Hrană și consumabile:</span>
                        <div className="flex-grow bg-gray-200 rounded-full h-2.5 max-w-sm">
                          <div className="bg-primary h-2.5 rounded-full" style={{ width: '40%' }}></div>
                        </div>
                        <span className="ml-2 text-gray-700">40%</span>
                      </li>
                      <li className="flex items-center">
                        <span className="text-gray-700 w-40">Servicii veterinare:</span>
                        <div className="flex-grow bg-gray-200 rounded-full h-2.5 max-w-sm">
                          <div className="bg-primary h-2.5 rounded-full" style={{ width: '30%' }}></div>
                        </div>
                        <span className="ml-2 text-gray-700">30%</span>
                      </li>
                      <li className="flex items-center">
                        <span className="text-gray-700 w-40">Întreținere adăpost:</span>
                        <div className="flex-grow bg-gray-200 rounded-full h-2.5 max-w-sm">
                          <div className="bg-primary h-2.5 rounded-full" style={{ width: '20%' }}></div>
                        </div>
                        <span className="ml-2 text-gray-700">20%</span>
                      </li>
                      <li className="flex items-center">
                        <span className="text-gray-700 w-40">Programe de educare:</span>
                        <div className="flex-grow bg-gray-200 rounded-full h-2.5 max-w-sm">
                          <div className="bg-primary h-2.5 rounded-full" style={{ width: '10%' }}></div>
                        </div>
                        <span className="ml-2 text-gray-700">10%</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-bold text-primary mb-4">Alte modalități de a ajuta</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                  <h4 className="font-bold text-primary mb-3">Donații în natură</h4>
                  <p className="text-gray-600 mb-4">
                    Acceptăm cu bucurie și donații sub formă de:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Hrană uscată și umedă pentru câini</span>
                    </li>
                    <li className="flex items-start">
                      <Check size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Paturi, pături și prosoape</span>
                    </li>
                    <li className="flex items-start">
                      <Check size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Jucării și accesorii</span>
                    </li>
                    <li className="flex items-start">
                      <Check size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Medicamente (cu termen de valabilitate valid)</span>
                    </li>
                  </ul>
                  <div className="mt-4">
                    <Button href="/contact" variant="outline">
                      Contactează-ne
                    </Button>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                  <h4 className="font-bold text-primary mb-3">Devino voluntar</h4>
                  <p className="text-gray-600 mb-4">
                    Timpul tău este la fel de valoros ca orice donație financiară. Poți ajuta la:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Plimbarea câinilor</span>
                    </li>
                    <li className="flex items-start">
                      <Check size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Socializarea și îngrijirea animalelor</span>
                    </li>
                    <li className="flex items-start">
                      <Check size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Organizarea de evenimente</span>
                    </li>
                    <li className="flex items-start">
                      <Check size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Promovarea adopțiilor online</span>
                    </li>
                  </ul>
                  <div className="mt-4">
                    <Button href="https://docs.google.com/forms/d/e/1FAIpQLSd09cTbvA9b6a-lxOh-GbnSZ7s2O032iSqNKrDQximxuwXYOA/viewform" variant="outline" external={true}>
                      Devino voluntar
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-lg shadow-md p-6 border border-gray-200 sticky top-24"
            >
              <div className="flex items-center mb-6">
                <Heart className="text-accent w-6 h-6 mr-3" />
                <h2 className="text-2xl font-bold text-primary font-display">Donează acum</h2>
              </div>
              <DonationForm />
            </motion.div>
          </div>
        </div>

        <div className="mt-16">
          <div className="bg-primary text-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <h3 className="text-2xl font-bold mb-4 font-display">Sponsorizează un câine</h3>
                  <p className="text-white/90 mb-6">
                    Dacă nu poți adopta un câine, poți totuși să devii "părintele la distanță" al unuia dintre câinii noștri. Cu o donație lunară, poți sprijini îngrijirea unui anumit câine și vei primi actualizări regulate despre progresul său.
                  </p>
                  <Button href="/caini" variant="white">
                    Alege un câine de sponsorizat
                  </Button>
                </motion.div>
              </div>
              <div>
                <img
                  src="https://images.pexels.com/photos/1452717/pexels-photo-1452717.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                  alt="Dog awaiting sponsorship"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default Donate;